/**
 * Copyright © 2026 Adnuntius AS.
 */
import {NgForm} from '@angular/forms';
import * as _ from 'lodash';

import {SubmitCalls} from '../directives/submit-calls';
import {interpolateTemplate} from '../filters/interpolate-template';

export interface FormControlError {
  control: any;
  key: string;
  apiText: string;
}

export interface FormSubmitState {
  validationError: boolean;
  validSubmission: boolean;
  apiErrors: Array<{ key: string; apiText: string }>;
  clientErrors: any[];
  hasValidationWarnings: boolean;
  bulkError?: boolean;
  /** Angular form used to map API warnings back to named controls. */
  ngForm?: NgForm;
}

export function createFormSubmitState(): FormSubmitState {
  return {
    validationError: false,
    validSubmission: false,
    apiErrors: [],
    clientErrors: [],
    hasValidationWarnings: false
  };
}

export function hasFormValidationError(state: FormSubmitState): boolean {
  return state.validationError;
}

export function clearFormValidation(state: FormSubmitState, formControlErrors: FormControlError[]): void {
  _.forEach(formControlErrors, (validationObj) => {
    const control = validationObj.control;
    if (control?.setErrors && control.errors?.[validationObj.key]) {
      const errors = {...control.errors};
      delete errors[validationObj.key];
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
  });
  formControlErrors.length = 0;
  state.apiErrors = [];
  state.clientErrors = [];
  state.validationError = false;
}

export function markFormSubmitSuccess(state: FormSubmitState, ngForm: NgForm): void {
  state.validationError = false;
  state.validSubmission = true;
  ngForm.control.markAsPristine();
}

export function markFormSubmitError(state: FormSubmitState, ngForm: NgForm): void {
  state.validationError = true;
  state.validSubmission = false;
  ngForm.control.markAsPristine();
}

function contextualiseApiText(context: any, apiText: string): string {
  if (!_.isString(context) || !context) {
    return apiText;
  }

  const contextParts = context.split('.').filter(Boolean);
  const fieldName = contextParts[contextParts.length - 1];
  const fieldLabel = _.startCase(fieldName.replace(/Id$/, ''));
  if (!fieldLabel) {
    return apiText;
  }
  return apiText ? fieldLabel + ': ' + apiText : fieldLabel;
}

export function handleApiValidationErrors(
  state: FormSubmitState,
  resource: any,
  errors: any,
  formControlErrors: FormControlError[],
  ngForm?: NgForm
): void {
  const findControl = (context?: string): any => {
    if (!_.isString(context) || !context) {
      return null;
    }

    const allContexts = [context];
    let truncatedContext = context;

    while (truncatedContext.indexOf('.') > -1) {
      truncatedContext = truncatedContext.substring(0, truncatedContext.lastIndexOf('.'));
      allContexts.push(truncatedContext);
    }

    for (const candidateContext of allContexts) {
      const fieldName = resource.resourceName + '.' + candidateContext;
      const fieldNameStandardised = 'resource.model.' + candidateContext;
      const fieldNameDirect = candidateContext;
      const control = ngForm?.controls?.[fieldName]
        || ngForm?.controls?.[fieldNameStandardised]
        || ngForm?.controls?.[fieldNameDirect];
      if (control) {
        return control;
      }
    }

    return null;
  };

  _.forEach(_.isArray(errors) ? errors : [errors], (error) => {
    const errorData = error.error || error;
    const context = error.context || errorData.context;
    const control = findControl(context);
    errorData.apiText = errorData.text ? interpolateTemplate(errorData.text)(errorData.parameters) : '';

    if (control) {
      control.setErrors({
        ...(control.errors || {}),
        [errorData.code]: errorData.apiText ? {apiText: errorData.apiText} : true
      });
      formControlErrors.push({
        control,
        key: errorData.code,
        apiText: errorData.apiText
      });
    }
    if (!control || errorData.withApi) {
      state.apiErrors.push({
        key: errorData.code,
        apiText: control ? errorData.apiText : contextualiseApiText(context, errorData.apiText)
      });
    }
  });
  state.validationError = true;
}

export function updateFormWarnings(state: FormSubmitState, model: any, isPureMarketplacePlatform?: boolean): void {
  SubmitCalls.updateFormWarnings(state, model, isPureMarketplacePlatform);
}

export interface FormSubmitStateAccessorOptions {
  getRegisteredForm?: () => NgForm | null;
  dismiss?: () => void;
}

export function exposeFormSubmitStateAccessors(
  host: object,
  state: FormSubmitState,
  options: FormSubmitStateAccessorOptions = {}
): void {
  Object.defineProperties(host, {
    validSubmission: {get: () => state.validSubmission, enumerable: true},
    apiErrors: {get: () => state.apiErrors, enumerable: true},
    clientErrors: {get: () => state.clientErrors, enumerable: true},
    hasValidationWarnings: {get: () => state.hasValidationWarnings, enumerable: true},
    bulkError: {get: () => state.bulkError, enumerable: true},
    hasValidationError: {get: () => hasFormValidationError(state), enumerable: true}
  });
  if (options.getRegisteredForm) {
    Object.defineProperty(host, 'registeredForm', {
      get: options.getRegisteredForm,
      enumerable: true
    });
  }
  Object.defineProperty(host, 'dismissSaveMessage', {
    value: options.dismiss || (() => { state.validSubmission = false; }),
    enumerable: true,
    writable: true
  });
}
